import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

interface IProps {
    html: string;
}

const DEFAULT_IFRAME_HEIGHT = 1;
export const ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE = "pg:article-widget:height";
const iframeBaseStyles = `
            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 300;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_light.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 400;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_regular.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 500;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_medium.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 600;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_semibold.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 700;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_bold.woff2') format('woff2');
            }

            html {
                font-size: 10px;
                line-height: 1.15;
                -webkit-text-size-adjust: 100%;
                -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            }

            html, body {
                margin: 0;
                padding: 0;
                background: transparent;
            }

            body {
                overflow-x: hidden;
                font-family: Poppins, 'Helvetica Neue', Helvetica, Arial, sans-serif;
                font-size: 1.4rem;
                font-weight: 400;
                line-height: 2rem;
                color: #23232d;
            }

            *, *::before, *::after {
                box-sizing: border-box;
            }

            input,
            button,
            select,
            textarea {
                font-family: inherit;
                font-size: inherit;
                line-height: inherit;
            }
        `;

export const extractWidgetBodyHtml = (html: string) => {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);

    return bodyMatch?.[1] ?? html;
};

export const buildArticleWidgetDocument = (html: string) => {
    const bodyHtml = extractWidgetBodyHtml(html);

    return `<!doctype html>
<html lang="pl">
    <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
${iframeBaseStyles}
        </style>
    </head>
    <body>
        ${bodyHtml}
        <script>
            (function() {
                const postHeight = function() {
                    const docEl = document.documentElement;
                    const body = document.body;
                    const height = Math.max(
                        docEl.scrollHeight,
                        docEl.offsetHeight,
                        docEl.clientHeight,
                        body ? body.scrollHeight : 0,
                        body ? body.offsetHeight : 0,
                        body ? body.clientHeight : 0
                    );

                    window.parent.postMessage(
                        {
                            type: "${ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE}",
                            height: height
                        },
                        "*"
                    );
                };

                const scheduleHeightSync = function() {
                    window.requestAnimationFrame(postHeight);
                };

                if (document.readyState === "complete" || document.readyState === "interactive") {
                    scheduleHeightSync();
                }

                document.addEventListener("DOMContentLoaded", scheduleHeightSync);
                window.addEventListener("load", scheduleHeightSync);
                window.addEventListener("resize", scheduleHeightSync);
                document.addEventListener("input", scheduleHeightSync, true);
                document.addEventListener("change", scheduleHeightSync, true);

                const observer = new MutationObserver(scheduleHeightSync);
                observer.observe(document.documentElement, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    characterData: true
                });

                setTimeout(scheduleHeightSync, 0);
                setTimeout(scheduleHeightSync, 100);
                setTimeout(scheduleHeightSync, 500);
            })();
        </script>
    </body>
</html>`;
};

export const ArticleWidget: React.FC<IProps> = ({html}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
    const widgetId = useId();
    const srcDoc = useMemo(() => buildArticleWidgetDocument(html), [html]);

    useEffect(() => {
        setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    }, [srcDoc]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            if (event.data?.type !== ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE || typeof event.data?.height !== "number") {
                return;
            }

            setIframeHeight(Math.max(DEFAULT_IFRAME_HEIGHT, Math.ceil(event.data.height)));
        };

        window.addEventListener("message", onMessage);

        return () => {
            window.removeEventListener("message", onMessage);
        };
    }, []);

    return (
        <iframe
            ref={iframeRef}
            title={`Artykułowy widget HTML ${widgetId}`}
            className={widgetIframeStyle}
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            scrolling="no"
            frameBorder="0"
            width="100%"
            height={String(iframeHeight)}
        />
    );
};

const widgetIframeStyle = css({
    display: "block",
    width: "100%",
    maxWidth: "100%",
    borderWidth: 0,
    overflow: "hidden",
    backgroundColor: "transparent"
});
import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

interface IProps {
    html: string;
}

const DEFAULT_IFRAME_HEIGHT = 1;
export const ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE = "pg:article-widget:height";
const iframeBaseStyles = `
            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 300;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_light.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 400;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_regular.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 500;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_medium.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 600;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_semibold.woff2') format('woff2');
            }

            @font-face {
                font-family: 'Poppins';
                font-style: normal;
                font-weight: 700;
                font-display: swap;
                src: url('/static-rp/fonts/poppins/poppins_bold.woff2') format('woff2');
            }

            html {
                font-size: 10px;
                line-height: 1.15;
                -webkit-text-size-adjust: 100%;
                -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            }

            html, body {
                margin: 0;
                padding: 0;
                background: transparent;
            }

            body {
                overflow-x: hidden;
                font-family: Poppins, 'Helvetica Neue', Helvetica, Arial, sans-serif;
                font-size: 1.4rem;
                font-weight: 400;
                line-height: 2rem;
                color: #23232d;
            }

            *, *::before, *::after {
                box-sizing: border-box;
            }

            input,
            button,
            select,
            textarea {
                font-family: inherit;
                font-size: inherit;
                line-height: inherit;
            }
        `;

export const extractWidgetBodyHtml = (html: string) => {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);

    return bodyMatch?.[1] ?? html;
};

export const buildArticleWidgetDocument = (html: string) => {
    const bodyHtml = extractWidgetBodyHtml(html);

    return `<!doctype html>
<html lang="pl">
    <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
${iframeBaseStyles}
        </style>
    </head>
    <body>
        ${bodyHtml}
        <script>
            (function() {
                const postHeight = function() {
                    const docEl = document.documentElement;
                    const body = document.body;
                    const height = Math.max(
                        docEl.scrollHeight,
                        docEl.offsetHeight,
                        docEl.clientHeight,
                        body ? body.scrollHeight : 0,
                        body ? body.offsetHeight : 0,
                        body ? body.clientHeight : 0
                    );

                    window.parent.postMessage(
                        {
                            type: "${ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE}",
                            height: height
                        },
                        "*"
                    );
                };

                const scheduleHeightSync = function() {
                    window.requestAnimationFrame(postHeight);
                };

                if (document.readyState === "complete" || document.readyState === "interactive") {
                    scheduleHeightSync();
                }

                document.addEventListener("DOMContentLoaded", scheduleHeightSync);
                window.addEventListener("load", scheduleHeightSync);
                window.addEventListener("resize", scheduleHeightSync);
                document.addEventListener("input", scheduleHeightSync, true);
                document.addEventListener("change", scheduleHeightSync, true);

                const observer = new MutationObserver(scheduleHeightSync);
                observer.observe(document.documentElement, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    characterData: true
                });

                setTimeout(scheduleHeightSync, 0);
                setTimeout(scheduleHeightSync, 100);
                setTimeout(scheduleHeightSync, 500);
            })();
        </script>
    </body>
</html>`;
};

export const ArticleWidget: React.FC<IProps> = ({html}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
    const widgetId = useId();
    const srcDoc = useMemo(() => buildArticleWidgetDocument(html), [html]);

    useEffect(() => {
        setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    }, [srcDoc]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            if (event.data?.type !== ARTICLE_WIDGET_HEIGHT_MESSAGE_TYPE || typeof event.data?.height !== "number") {
                return;
            }

            setIframeHeight(Math.max(DEFAULT_IFRAME_HEIGHT, Math.ceil(event.data.height)));
        };

        window.addEventListener("message", onMessage);

        return () => {
            window.removeEventListener("message", onMessage);
        };
    }, []);

    return (
        <iframe
            ref={iframeRef}
            title={`Artykułowy widget HTML ${widgetId}`}
            className={widgetIframeStyle}
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            scrolling="no"
            frameBorder="0"
            width="100%"
            height={String(iframeHeight)}
        />
    );
};

const widgetIframeStyle = css({
    display: "block",
    width: "100%",
    maxWidth: "100%",
    borderWidth: 0,
    overflow: "hidden",
    backgroundColor: "transparent"
});
